import { ArrowDownIcon, ArrowUpIcon, PlusIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { useProject } from "@/lib/project";
import type { EpicListItem, SprintListItem, TaskListItem } from "@/lib/types";
import { cn, formatDateTime, formatDuration, taskKey, timeAgo, totalSeconds } from "@/lib/utils";
import { PropertyValue, ValueButton } from "../properties/Properties";
import { StageBadge } from "../ui";
import { AssigneeList, RunningDot } from "./Assignees";
import { useTaskActions } from "./hooks";
import { StagePicker } from "./pickers";
import type { ViewState } from "./viewState";

function Th({ label, sortKey, state, onSort, className }: { label: string; sortKey?: string; state: ViewState; onSort: (key: string) => void; className?: string }) {
  const active = sortKey && state.sort.key === sortKey;
  return (
    <th className={cn("h-9 border-e border-b border-line px-2 text-start text-[13px] font-normal whitespace-nowrap text-muted", className)}>
      {sortKey ? (
        <button type="button" onClick={() => onSort(sortKey)} className="flex items-center gap-1 hover:text-fg">
          {label}
          {active && (state.sort.dir === "asc" ? <ArrowUpIcon className="size-3" /> : <ArrowDownIcon className="size-3" />)}
        </button>
      ) : (
        label
      )}
    </th>
  );
}

export function TaskTable({
  tasks,
  state,
  onSort,
  onOpen,
  onCreate,
  epics,
  sprints,
  projectScope,
}: {
  tasks: TaskListItem[];
  state: ViewState;
  onSort: (key: string) => void;
  onOpen: (id: string) => void;
  onCreate?: (title: string) => Promise<void>;
  epics?: EpicListItem[];
  sprints?: SprintListItem[];
  projectScope: boolean;
}) {
  const ctx = useProject();
  const { update } = useTaskActions();
  const props = ctx.propsFor("task");
  const epicTitle = new Map(epics?.map((e) => [e.id, e.title]));
  const sprintTitle = new Map(sprints?.map((s) => [s.id, s.title]));
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  return (
    <div className="overflow-x-auto px-6 pb-8 md:px-12 scroll-thin">
      <table className="w-max min-w-full border-separate border-spacing-0 border-t border-line text-sm">
        <thead>
          <tr>
            <Th label="Key" sortKey="number" state={state} onSort={onSort} className="w-20" />
            <Th label="Title" sortKey="title" state={state} onSort={onSort} className="min-w-72" />
            <Th label="Stage" sortKey="stage" state={state} onSort={onSort} />
            <Th label="Assignees" state={state} onSort={onSort} className="min-w-40" />
            {projectScope && <Th label="Epic" state={state} onSort={onSort} className="min-w-40" />}
            {projectScope && <Th label="Sprint" state={state} onSort={onSort} className="min-w-32" />}
            {props.map((p) => (
              <Th key={p.id} label={p.name} sortKey={p.id} state={state} onSort={onSort} className="min-w-40" />
            ))}
            <Th label="Time on task" sortKey="time" state={state} onSort={onSort} />
            <Th label="In Progress started" state={state} onSort={onSort} />
            <Th label="QA reached" state={state} onSort={onSort} />
            <Th label="Last edited" sortKey="updated" state={state} onSort={onSort} className="border-e-0" />
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => {
            const stage = ctx.stageById.get(t.stageId);
            const running = t.time.some((e) => e.openSince);
            return (
              <tr key={t.id} className="group hover:bg-hover/50">
                <td className="border-e border-b border-line px-2 font-mono text-xs whitespace-nowrap text-faint">{taskKey(ctx.bundle.project.key, t.number)}</td>
                <td className="border-e border-b border-line p-0">
                  <button type="button" onClick={() => onOpen(t.id)} className="block min-h-9 w-full px-2 py-2 text-start font-medium hover:underline" dir="auto">
                    {t.title}
                  </button>
                </td>
                <td className="border-e border-b border-line p-0">
                  <StagePicker
                    value={t.stageId}
                    onChange={(stageId) => update(t.id, { stageId })}
                    trigger={(tr) => (
                      <ValueButton ref={tr.ref} onClick={tr.onClick} disabled={!ctx.canEdit} className="min-h-9 flex-nowrap rounded-none">
                        {stage && <StageBadge color={stage.color}>{stage.name}</StageBadge>}
                      </ValueButton>
                    )}
                  />
                </td>
                <td className="border-e border-b border-line px-2">
                  <AssigneeList task={t} />
                </td>
                {projectScope && (
                  <td className="max-w-56 truncate border-e border-b border-line px-2 text-muted" dir="auto">
                    {epicTitle.get(t.epicId)}
                  </td>
                )}
                {projectScope && (
                  <td className="max-w-48 truncate border-e border-b border-line px-2 text-muted" dir="auto">
                    {t.sprintId ? sprintTitle.get(t.sprintId) : <span className="text-faint">Backlog</span>}
                  </td>
                )}
                {props.map((p) => (
                  <td key={p.id} className="max-w-72 border-e border-b border-line p-0">
                    <PropertyValue property={p} entityId={t.id} values={t.values} variant="cell" />
                  </td>
                ))}
                <td className="border-e border-b border-line px-2 whitespace-nowrap tabular-nums">
                  <span className="flex items-center gap-1.5">
                    {running && <RunningDot />}
                    {t.time.length ? formatDuration(totalSeconds(t.time)) : ""}
                  </span>
                </td>
                <td className="border-e border-b border-line px-2 whitespace-nowrap text-muted">{formatDateTime(t.inProgressAt)}</td>
                <td className="border-e border-b border-line px-2 whitespace-nowrap text-muted">{formatDateTime(t.qaReachedAt)}</td>
                <td className="border-b border-line px-2 whitespace-nowrap text-muted" title={formatDateTime(t.updatedAt)}>
                  {timeAgo(t.updatedAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {ctx.canEdit && onCreate && (
        <form
          className="flex h-9 items-center gap-2 border-b border-line px-2 text-muted"
          onSubmit={async (e) => {
            e.preventDefault();
            const v = title.trim();
            if (!v) return;
            setSaving(true);
            try {
              await onCreate(v);
              setTitle("");
            } finally {
              setSaving(false);
            }
          }}
        >
          <PlusIcon className="size-4" />
          <input
            value={title}
            readOnly={saving}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="New task — type a title and press Enter"
            className="flex-1 bg-transparent outline-none placeholder:text-faint"
            dir="auto"
          />
        </form>
      )}
      {tasks.length === 0 && <div className="py-10 text-center text-sm text-muted">No tasks match this view</div>}
    </div>
  );
}
